(() => {
  const ELEMENT_NAME = "collection-grid";

  if (customElements.get(ELEMENT_NAME)) return;

  const onIdle =
    window.requestIdleCallback ?? ((callback) => setTimeout(callback, 1));

  class CollectionGrid extends HTMLElement {
    #grid = null;
    #next = null;
    #previous = null;
    #count = null;
    #busy = false;
    /** @type {Map<string, Promise<Document>>} url with section_id -> in-flight or settled request */
    #pages = new Map();
    #boundOnClick = this.#onClick.bind(this);
    #boundOnPrefetch = this.#onPrefetch.bind(this);
    #boundOnPrefetchAll = () => {
      this.#prefetch(this.#next);
      this.#prefetch(this.#previous);
    };

    connectedCallback() {
      this.#grid = this.querySelector("[data-grid]");
      this.#next = this.querySelector("[data-load-more]");
      this.#previous = this.querySelector("[data-load-previous]");
      this.#count = this.querySelector("[data-count]");

      this.#addListeners(this.#next);
      this.#addListeners(this.#previous);

      // Each page costs a full section render server-side, so warm them while
      // the shopper is still scrolling rather than on click.
      onIdle(this.#boundOnPrefetchAll);
    }

    disconnectedCallback() {
      this.#removeListeners(this.#next);
      this.#removeListeners(this.#previous);
      this.#pages.clear();
    }

    get #sectionId() {
      return this.getAttribute("section-id");
    }

    #addListeners(link) {
      link?.addEventListener("click", this.#boundOnClick);
      link?.addEventListener("pointerenter", this.#boundOnPrefetch);
      link?.addEventListener("focus", this.#boundOnPrefetch);
    }

    #removeListeners(link) {
      link?.removeEventListener("click", this.#boundOnClick);
      link?.removeEventListener("pointerenter", this.#boundOnPrefetch);
      link?.removeEventListener("focus", this.#boundOnPrefetch);
    }

    /**
     * Intercepts a paginated link and grows the grid in place, forwards for
     * "Load more" and backwards for "Load previous". Without JS both links
     * still navigate to their page.
     */
    async #onClick(event) {
      const link = event.currentTarget;

      if (!this.#grid || !this.#sectionId || !link.href) return;

      event.preventDefault();

      if (this.#busy) return;

      const url = link.href;
      const isPrevious = link.hasAttribute("data-load-previous");

      this.#setBusy(link, true);

      try {
        const page = await this.#requestPage(url);

        if (isPrevious) {
          this.#prependCards(page);
        } else {
          this.#appendCards(page);
          this.#syncUrl(url);
        }

        this.#syncControl(page, isPrevious);
        this.#syncCount();
      } catch {
        // The click was already prevented, so a failed fetch would otherwise
        // leave the shopper on a dead link. Fall back to the navigation the
        // link performs without JS.
        window.location.assign(url);
      } finally {
        this.#setBusy(link, false);
      }

      onIdle(this.#boundOnPrefetchAll);
    }

    #onPrefetch(event) {
      this.#prefetch(event.currentTarget);
    }

    #prefetch(link) {
      if (!link?.href || !this.#sectionId) return;

      this.#requestPage(link.href).catch(() => {
        // Prefetch failures are silent — the click path reports them.
      });
    }

    /**
     * Fetches a page of this section once and reuses the result. The link URL
     * already carries the active query string, so filters and sorting are
     * preserved once added.
     */
    #requestPage(pageUrl) {
      const url = new URL(pageUrl, window.location.href);
      url.searchParams.set("section_id", this.#sectionId);

      const key = url.toString();
      const cached = this.#pages.get(key);

      if (cached) return cached;

      const request = fetch(key)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, "text/html"));

      request.catch(() => this.#pages.delete(key));
      this.#pages.set(key, request);

      return request;
    }

    #appendCards(page) {
      const cards = page.querySelectorAll("[data-grid] > [data-card]");

      if (cards.length === 0) return;

      this.#grid.append(...cards);
    }

    /**
     * The control sits before the grid, so prepending pushes the cards the
     * shopper was reading down the page while leaving the button itself pinned
     * where it was. Scrolling by however far the old content moved holds their
     * place and puts the older products above the fold, to be scrolled back to.
     *
     * Browser scroll anchoring cannot be relied on to do this: it is suppressed
     * at scroll offset 0, which is exactly where a shopper sits when they reach
     * a control at the top of the list, and Safari has none at all. It is
     * switched off for this one mutation so the correction below is the only
     * adjustment applied, then restored — leaving it off would let the grid's
     * siblings become anchors and drag the viewport on every append.
     */
    #prependCards(page) {
      const cards = page.querySelectorAll("[data-grid] > [data-card]");
      const anchor = this.#grid.firstElementChild;

      if (cards.length === 0) return;

      if (!anchor) {
        this.#grid.prepend(...cards);
        return;
      }

      const topBefore = anchor.getBoundingClientRect().top;

      this.#grid.style.overflowAnchor = "none";
      this.#grid.prepend(...cards);

      // Forces the layout that settles the new cards before the offset is read.
      const topAfter = anchor.getBoundingClientRect().top;

      window.scrollBy({ top: topAfter - topBefore, behavior: "instant" });
      this.#grid.style.overflowAnchor = "";
    }

    /**
     * Points the link at the page beyond the one just loaded. When that
     * direction runs out the control is removed.
     */
    #syncControl(page, isPrevious) {
      const link = isPrevious ? this.#previous : this.#next;
      const replacement = page.querySelector(
        isPrevious ? "[data-load-previous]" : "[data-load-more]",
      );

      if (!link) return;

      if (replacement) {
        link.href = replacement.getAttribute("href");
        return;
      }

      this.#removeListeners(link);

      if (isPrevious) {
        // The whole row is the control, unlike "Load more" which shares its
        // row with the count.
        link.closest("[data-pagination-previous]")?.remove();
        this.#previous = null;
        return;
      }

      link.remove();
      this.#next = null;
    }

    /**
     * The server renders a per-page count, since the grid can be entered at any
     * page. Once cards are added in either direction the DOM is the count.
     */
    #syncCount() {
      const template = this.#count?.getAttribute("data-count-text");

      if (!template) return;

      this.#count.textContent = template.replace(
        "{count}",
        this.#grid.children.length,
      );
    }

    /**
     * Leaves the deepest loaded page in the address bar so a reload or a shared
     * link lands on it, and the back button returns near where the shopper
     * left. replaceState rather than pushState: one entry for the collection,
     * not one per batch loaded.
     */
    #syncUrl(pageUrl) {
      const url = new URL(pageUrl, window.location.href);
      url.searchParams.delete("section_id");

      window.history.replaceState(window.history.state, "", url);
    }

    #setBusy(link, busy) {
      this.#busy = busy;
      link.setAttribute("aria-busy", busy ? "true" : "false");
    }
  }

  customElements.define(ELEMENT_NAME, CollectionGrid);
})();
